import type { AppApi, EDITOR_TYPE } from '@wix/wix-data-client-common'
import {
  getPageName,
  isDbDriverCollection,
  PAGE_TYPES,
} from '@wix/wix-data-client-common'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import type { Schema } from '@wix/wix-data-schema-types'
import type Experiments from '@wix/wix-experiments'
import { first } from 'lodash'
import { getPresetDefinitionById } from '../../presets/getPresetDefinitionById'
import { generateRouteDefinition } from '../add-dynamic-pages/new-page/generateRouteDefinition'
import { prepareDataForPages } from '../add-dynamic-pages/prepareDataForPages'
import { shouldUseSeoV2RouteConfig } from '../useSeoV2Infra'
import { getCollectionFields } from '../utils/getCollectionFields'
import { generateUniquePageLinkId } from '../../slug/generatePageLinkId'
import { MAIN_SLUG_FIELD_KEY } from '../../slug/constants'

export const prepareDataForBlankPage = async ({
  editorSdkProxy,
  appApi,
  schema,
  experimentsManager,
  editorType,
  isSlugFeatureEnabled,
}: {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  schema: Schema
  experimentsManager: Experiments
  editorType: EDITOR_TYPE
  isSlugFeatureEnabled: boolean
}) => {
  const preset = await getPresetDefinitionById(
    'blank',
    editorType,
    editorSdkProxy,
  )

  const presetRouter = first(preset.routers)
  const routerConfig = presetRouter!.config

  const { collectionFields } = getCollectionFields({
    schema,
    isFromExistingCollection: true,
  })

  const { routerPrefix, pageLinks } = await prepareDataForPages({
    appApi,
    editorSdkProxy,
    collectionId: schema.id,
    collectionName: schema.displayName,
    pagesToAdd: [{ key: 'itemPage' }],
    collectionFields,
    displayField: schema.displayField,
    isTPACollection: isDbDriverCollection(schema),
    pageType: PAGE_TYPES.ITEM,
    isEditPage: false,
  })

  const pattern = isSlugFeatureEnabled
    ? `/{${MAIN_SLUG_FIELD_KEY}}`
    : pageLinks.itemPage.pattern

  const presetPage = preset.pages.find(p => p.type === 'itemPage')

  const useSeoV2RouteConfig = await shouldUseSeoV2RouteConfig({
    editorSdkProxy,
    experimentsManager,
  })

  const pageLinkId = isSlugFeatureEnabled
    ? await generateUniquePageLinkId({ editorSdkProxy })
    : undefined

  const routeDefinition = generateRouteDefinition({
    schema,
    editorType,
    collectionId: schema.id,
    routerConfig,
    routerPages: presetRouter!.pages,
    pageId: presetPage!.id,
    route: pattern,
    isIndexable: true,
    requiresAdjustingToSchema: true,
    seoV2: useSeoV2RouteConfig,
    pageLinkId,
  })

  const pageTitle = getPageName({
    collectionName: schema.displayName,
    pattern,
  })

  return { routeDefinition, pageTitle, routerPrefix, pageLinks, pattern }
}
